import {
  InputBase,
  Grid,
  Paper,
  MenuItem,
  MenuList,
  ClickAwayListener,
  Fade,
  Box,
  Typography,
  styled,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo, useState, useEffect } from "react";
import Fuse from "fuse.js";
import { useTranslation } from "react-i18next";
import { Search as SearchIcon, Close } from "@mui/icons-material";

const StyledInput = styled(InputBase)(({ theme }) => ({
  width: "100%",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  transition: theme.transitions.create(["box-shadow", "border-color"]),
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
  "&.Mui-focused": {
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.primary.main,
  },
  padding: theme.spacing(1, 2),
  fontSize: theme.typography.body1.fontSize,
}));

const SearchBar = ({ onSearch, attendees, role, placeholder }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const fuse = useMemo(
    () =>
      new Fuse(attendees, {
        keys: ["name", "designation", "company"],
        threshold: 0.3,
        includeMatches: true,
      }),
    [attendees]
  );

  const suggestions = useMemo(
    () => (searchQuery ? fuse.search(searchQuery).slice(0, 5) : []),
    [searchQuery, fuse]
  );

  const handleSearch = (value) => {
    setSearchQuery(value);
    onSearch(value);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.item.name);
    onSearch(suggestion.item.name);
    setShowSuggestions(false);
  };

  const highlightMatch = (text, matches) => {
    if (!matches) return text;
    const result = [];
    let lastIndex = 0;

    matches.forEach(([start, end]) => {
      result.push(text.slice(lastIndex, start));
      result.push(<strong key={start}>{text.slice(start, end + 1)}</strong>);
      lastIndex = end + 1;
    });

    result.push(text.slice(lastIndex));
    return result;
  };

  return (
    <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
      <Box position="relative" sx={{ width: "100%" }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <StyledInput
              fullWidth
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => {
                setIsFocused(true);
                setShowSuggestions(true);
              }}
              onBlur={() => setIsFocused(false)}
              placeholder={
                placeholder ||
                t("searchBar.searchPlaceholder", { role: t(role) })
              }
              startAdornment={<SearchIcon color="action" sx={{ mr: 1 }} />}
              endAdornment={
                searchQuery && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery("");
                      onSearch("");
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                )
              }
              aria-label={t("searchBar.ariaLabel")}
            />
          </Grid>
        </Grid>

        <Fade in={showSuggestions && suggestions.length > 0}>
          <Paper
            sx={{
              position: "absolute",
              zIndex: 1300,
              width: "100%",
              mt: 1,
              maxHeight: 300,
              overflow: "auto",
              boxShadow: 3,
              borderRadius: 2,
            }}
          >
            <MenuList dense>
              {suggestions.map((suggestion, index) => (
                <MenuItem
                  key={`${suggestion.item.id}-${index}`}
                  onClick={() => handleSuggestionClick(suggestion)}
                  sx={{
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                    py: 1.5,
                    px: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" noWrap>
                      {highlightMatch(
                        suggestion.item.name,
                        suggestion.matches?.[0]?.indices
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {suggestion.item.designation} • {suggestion.item.company}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </Fade>

        {/* {showSuggestions && searchQuery && suggestions.length === 0 && (
          <Paper sx={{
            position: 'absolute',
            zIndex: 1300,
            width: '100%',
            mt: 1,
            p: 2,
            boxShadow: 3,
            borderRadius: 2
          }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {t('searchBar.noResults')}
            </Typography>
          </Paper>
        )} */}
      </Box>
    </ClickAwayListener>
  );
};

SearchBar.propTypes = {
  onSearch: PropTypes.func.isRequired,
  attendees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      designation: PropTypes.string,
      company: PropTypes.string,
    })
  ).isRequired,
  role: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
};

export default SearchBar;
